import { useTranslation } from '@Providers/TranslationProvider';
import { type V2AboutUsPageType } from '@Src/middlewares/CustomPageTypes';
import Grid from '@mui/material/Grid';
import Highlight from '../Highlight';
import { StyledSection } from '../Common/StyledSection';
import Gallery from '@Components/Widgets/Gallery';
import Typography from '@mui/material/Typography';
import { type Theme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

type Props = {
  isPage?: boolean;
  data?: V2AboutUsPageType;
};

type StyledTitleGridProps = {
  ownerState: {
    noPaddingBottom?: boolean;
  };
};
const StyledTitleGrid = styled(Grid)<StyledTitleGridProps>`
  color: ${({ theme }) => theme.palette.text.primary};
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ ownerState, theme }) =>
      ownerState?.noPaddingBottom ? 0 : theme.spacing(2)};
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-left: ${({ theme }) => theme.spacing(10)};
    padding-right: ${({ theme }) => theme.spacing(10)};
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-left: ${({ theme }) => theme.spacing(14)};
    padding-right: ${({ theme }) => theme.spacing(14)};
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding-left: ${({ theme }) => theme.spacing(18)};
    padding-right: ${({ theme }) => theme.spacing(18)};
  }
`;
type StyledAboutUsSectionProps = {
  ownerState: {
    noTopMargin?: boolean;
  };
};
const StyledAboutUsSection = styled(StyledSection)<StyledAboutUsSectionProps>`
  padding-top: 0 !important;
  .carousel-root {
    margin-top: ${({ ownerState }) =>
      ownerState?.noTopMargin ? '0 !important' : 'inherit'};
  }
`;

export const AboutUs = (props: Props): JSX.Element => {
  const { data } = props;
  const { translate } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  if (!data) {
    return <></>;
  }

  const blocks = data.blocks;
  const blockIds =
    Object.keys(data.blocks)
      ?.filter((id) => !blocks?.[id]?.isHidden)
      ?.sort((a, b) => (blocks?.[a]?.order || 0) - (blocks?.[b]?.order || 0)) ||
    [];

  const firstBlockOnlyImage =
    (data?.blocks?.[0]?.images?.length || 0) > 0 &&
    !data?.blocks?.[0]?.description;

  return (
    <StyledAboutUsSection ownerState={{ noTopMargin: firstBlockOnlyImage }}>
      <Grid container>
        <StyledTitleGrid
          ownerState={{ noPaddingBottom: firstBlockOnlyImage }}
          item
          display={'flex'}
          alignItems={'flex-start'}
          xs={12}
          alignSelf={'stretch'}
        >
          <Typography variant={isSm ? 'h2Display' : 'h1'}>
            {translate(data?.title || '')}
          </Typography>
        </StyledTitleGrid>
        {blockIds?.map((blockId, index) => (
          <Highlight
            orientation={index % 2 === 0 ? 'right' : 'left'}
            hasBackground={index === 0 && !!blocks[blockId]?.description}
            key={index}
            data={blocks[blockId]}
          />
        ))}
      </Grid>
      {data.galleryEnabled && <Gallery />}
    </StyledAboutUsSection>
  );
};

export default AboutUs;
